import { DropResult } from "@hello-pangea/dnd";
import exp from "constants";
export interface FormApiResponse {
  data: {
    form: Form;
    metaData: MetadataField[];
  };
}
export interface Form {
  id: number;
  title: string;
  name?: string;
  description: string;
  scope: Scope;
  formPages: FormPage[];
}
export interface FormPage {
  id: number;
  title: string;
  description?: string;
  pageNumber?: number;
  formRows: FormRow[];
}
export interface FormRow {
  id: string;
  layout: 'SINGLE' | 'MULTIPLE';
  formFields: FormFieldTest[];
}
export interface FormFieldTest {
  id: number;
  field: Field | null;
  component: Component | null;
  alignment?: 0 | 1 | 2;
  componentId?: number;
  componentName?: string;
  layoutType?: 'row' | 'column';
  isRequired?: boolean;
  config?: FieldConfig | null;
}
export interface Field {
  id: number;
  groupName?: string;
  name: string;
  value?: any | null;
  dataType: string;
  accessKey: string;
  fieldType: string;
  importId?: string | null;
  source?: string;
  config: FieldConfig;
  scope?: FieldScope;
  scopeId?: number;
  statusId?: number;
  isDeleted?: boolean;
  isStandard?: boolean;
  companyId?: number;
  accountId?: number;
  createdAt?: string;
  updatedAt?: string;
  label?: string;
  icon?: React.ReactNode;
  key?: string;
}
export interface Component {
  id: number;
  name?: string;
  description?: string;
  createdAt: string;
  isStandard: boolean;
  account?: Account;
  componentFields: ComponentField[];
  scope?: Scope | null;
}
export type FieldType =
  | 'NAME'
  | 'FIRST_NAME'
  | 'LAST_NAME'
  | 'EMAIL'
  | 'PHONE_NO'
  | 'MOBILE_NO'
  | 'WEBSITE'
  | 'ADDRESS'
  | 'BIO'
  | 'DATE_OF_BIRTH'
  | 'NATIONAL_ID'
  | 'CITY'
  | 'ZIP_CODE'
  | 'STATE'
  | 'COUNTRY'
  | 'COMPANY_NAME'
  | 'DEPARTMENT'
  | 'DESIGNATION'
  | 'INDUSTRY'
  | 'RESUME'
  | 'EXPERIENCE_LEVEL'
  | 'SKILLS'
  | 'LINKEDIN'
  | 'GITHUB'
  | 'PORTFOLIO'
  | 'WHATSAPP'
  | 'TWITTER'
  | 'CONTACT_METHOD'
  | 'PREFERRED_TIME'
  | 'TIMEZONE'
  | 'LANGUAGE'
  | 'DOB'
  | 'JOINING_DATE'
  | 'AVAILABILITY_DATE'
  | 'RESIGNATION_DATE'
  | 'CURRENCY'
  | 'SALARY'
  | 'INCOME'
  | 'BUDGET'
  | 'DISCOUNT'
  | 'TAX_PERCENTAGE'
  | 'STATUS'
  | 'TAGS'
  | 'GENDER'
  | 'MARITAL_STATUS'
  | 'ACCESS_LEVEL'
  | 'AGREEMENT'
  | 'CONSENT'
  | 'PROFILE_PICTURE'
  | 'NOTES'
  | 'DESCRIPTION'
  | 'HEADING'
  | 'PARAGRAPH'
  | 'DIVIDER';


//////////////////////////////////////////////////////////////////////////////////
export interface Column {
  field?: string;
  id: string;
  label: string;
  isSortable?: boolean;
  isFlag?: boolean;
  isTooltip?: boolean;
  TooltipText?: string;
  isTag?: boolean;
  isCustomStandardTags?: boolean;
}
export interface TableConfig {
  dynamicColumns: Column[];
  visibleColumns: string[];
  page: number;
  perPage: number;
  sort?: { field: string; direction: 'ASC' | 'DESC' };
  filters: {
    status?: string[];
    role?: string[];
  };
  query: string;
  columnOrder: string[];
}
export interface DataTableProps {
  columns: Column[] | undefined;
  data: any[];
  columnOrder: string[];
  visibleColumns: string[];
  selectedIds?: number[];
  sort?: { field: string; direction: 'ASC' | 'DESC' };
  onDragEnd: (result: DropResult) => void;
  onSort?: (field: string) => void;
  showCheckboxColumn?: boolean;
  onSelectAll?: (ids: number[]) => void;
  onSelectRow?: (id: number) => void;
  onAction: (action: ActionType, id: string, state?: string, isStandard?: boolean) => void;
  loading: boolean;
  isFetching?: boolean;
  isFetchComplete?: boolean;
  perPage: number;
  noDataTitle?: string;
  noAction?: boolean;
  minWidth?: string;
  noDataSubtitle?: string;
  forms?: boolean;
  components?: boolean;
  isFields?: boolean;
  title?: string;
  hideIdColumn?: boolean;
  profiledName?: boolean;
  dynamicColumnsListing?: boolean;
  skeletonHeight?: string;
  routeKey?: string;
  hideActionColumn?: boolean;
  containerHeight?: number;
  filterHeight?: number;
  loadingID?: number | null;
  istableCellSpacing?: boolean;
}
export type ActionType = "sync" | "view" | "edit" | "delete" | "share" | "copy" | "revoke" | "condition" | "ignore" | "duplicate";
export interface PresignedUrlResponse {
  url: string;
  key: string;
}
export interface ContactPayload {
  prefix?: string;
  firstName: string;
  lastName: string;
  roleId?: number;
  organizationId?: number;
  email: string;
  phone?: string;
  image?: string;
  formDetails: {
    formId: number;
    fields: Array<{
      id: number;
      value: string;
    }>;
  };
}
export interface OrganizationPayload {
  name: string,
  website: string,
  branchName: string,
  industry: number,
  employeeRange: string,
  postalCode: string,
  description: string,
  formDetails: {
    formId: number;
    fields: Array<{
      id: number;
      value: string;
    }>;
  };
}

export interface ContactRolesPayload {
  id: number,
  name: string,
  roleAccessKey: string,
  statusId: number,
  isDeleted: boolean,
  isStandard: boolean,
  isAdmin: boolean,
  autoAssign: boolean,
  accountId: number,
  companyId: number,
  createdAt: Date,
  updatedAt: Date,
  rolePermissions: [],
}

interface BaseEntityService<T> {
  getEntityById: (id: number) => Promise<T>;
  createEntity: (data: T) => Promise<T>;
  updateEntity: (id: number, data: T) => Promise<T>;
  getPresignedUrl: (fileName: string, fileType: string) => Promise<{ url: string; key: string }>;
  uploadImage: (url: string, file: File) => Promise<void>;
}
export interface ContactService extends BaseEntityService<ContactPayload> {
  getPresignedUrl(fileName: string, fileType: string): Promise<any>;
  uploadImage(url: string, file: File): Promise<void>;
  createContact(contactData: any): Promise<any>;
  updateContact(id: number, contactData: any): Promise<any>;
  getOrganizations(): Promise<any>;
  getContactRoles(): Promise<any>;
  getContacts(
    page: number,
    perPage: number,
    sort?: { field: string; direction: 'ASC' | 'DESC' },
    query?: string,
    filters?: Record<string, any>,
    visibleColumns?: string[]
  ): Promise<any>;
  getContactById(id: number): Promise<any>;
  deleteContact(id: string): Promise<any>;
  getEntityById(id: number): Promise<any>;
  createEntity(contactData: ContactPayload): Promise<any>;
  updateEntity(id: number, contactData: ContactPayload): Promise<any>;
  getDynamicColumns: (scope: string, query?: string, page?: number, perPage?: number) => Promise<any>;
  getDynamicFilters: (scope: string) => Promise<any>;

  // Portal access / Cognito-related actions
  grantPortalAccess(id: number): Promise<any>;
  suspendPortalAccess(id: number): Promise<any>;
  reactivatePortalAccess(id: number): Promise<any>;
  resendPortalInvite(id: number): Promise<any>;
  resetPortalPassword(id: number): Promise<any>;
  getPortalAccessStatus(id: number): Promise<any>;

}
export interface OrganizationService extends BaseEntityService<OrganizationPayload> {
  getPresignedUrl(fileName: string, fileType: string): Promise<any>;
  uploadImage(url: string, file: File): Promise<void>;
  // getOrganizations(
  //   page: number,
  //   perPage: number,
  //   sort?: { field: string; direction: 'ASC' | 'DESC' },
  //   query?: string,
  //   filters?: Record<string, any>,
  //   visibleColumns?: string[]
  // ): Promise<any>;
  getOrganizationsMinimal(): Promise<any>;
  getOrganizationById(id: number): Promise<any>;
  createOrganization(organizationData: any): Promise<any>;
  updateOrganization(id: number, organizationData: any): Promise<any>;
  deleteOrganization(id: number): Promise<any>;
  getIndustries(): Promise<any>;
  getEntityById(id: number): Promise<any>;
  createEntity(organizationData: OrganizationPayload): Promise<any>;
  updateEntity(id: number, organizationData: OrganizationPayload): Promise<any>;
  getDynamicColumns: (scope: string, query?: string, page?: number, perPage?: number) => Promise<any>;
  getDynamicFilters: (scope: string) => Promise<any>;
}

export interface ContactRoles {
  createRole(roleData: any): Promise<any>;
  getRoleById(id: number): Promise<any>;
  updateRole(id: number, roleData: any): Promise<any>;
  revokePermissions(id: string): Promise<any>;
  getContactRoles: (
    page: number,
    perPage: number,
    sort?: { field: string; direction: 'ASC' | 'DESC' },
    query?: string,
    filters?: Record<string, any>,
    visibleColumns?: string[]
  ) => Promise<any>;
  getAllPermissions(): Promise<any>
}

///Form Interfaces
export interface MetaOption {
  label: string;
  value: string;
}
export interface MetadataField {
  field: string;
  label: string;
  dataType: string;
  key: string;
  source: string;
  options?: MetaOption[];
  isSortable: boolean;
  isRequired?: boolean;
}
interface ComponentField {
  id: number;
  componentId: number;
  fieldId: number;
  position: number;
  field: Field;
}
interface Account {
  firstName: string;
  lastName: string;
  image: string;
  signedUrl: string;
}
export interface PreviewField {
  key: string;
  label: string;
  config?: any;
  fieldType?: string;
}

export interface PreviewPage {
  id: number;
  title: string;
  description?: string;
  fields: PreviewField[];
}
export interface FormField {
  id: number;
  fieldId: number;
  key: string;
  label: string;
  name: string;
  field?: Field;
  config: FieldConfig;
  formFieldId?: number;
  type?: string;
  fieldType: FieldType;
  groupName?: string;
  subType?: string;
  position?: number;
  layoutType?: 'row' | 'column';
  icon?: React.ReactNode;
  alignment?: 'left' | 'right';
}


export type OutletContextType = {
  setSelectedEntity?: (
    type: "components",
    id: number
  ) => void;
  setRefreshTrigger?: React.Dispatch<React.SetStateAction<number>>;
  refreshTrigger?: number;
};


export enum FieldGroupEnum {
  PERSONAL_INFO = "PERSONAL_INFO",
  ADDRESS = "ADDRESS",
  EMPLOYMENT = "EMPLOYMENT",
  MEDIA = "MEDIA",
  OTHER_INFO = "OTHER_INFO",
  FINANCE = "FINANCE",
  LEGAL = "LEGAL",
  ONLINE_PRESENCE = "ONLINE_PRESENCE"
}

export enum FieldTypeEnum {
  // Personal Information
  NAME = 'NAME',
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  EMAIL = 'EMAIL',
  PHONE_NO = 'PHONE_NO',
  MOBILE_NO = 'MOBILE_NO',
  WEBSITE = 'WEBSITE',
  ADDRESS = 'ADDRESS',
  BIO = 'BIO',
  DATE_OF_BIRTH = 'DATE_OF_BIRTH',
  NATIONAL_ID = 'NATIONAL_ID',

  // Address Related
  CITY = 'CITY',
  ZIP_CODE = 'ZIP_CODE',
  STATE = 'STATE',
  COUNTRY = 'COUNTRY',

  // Professional
  COMPANY_NAME = 'COMPANY_NAME',
  DEPARTMENT = 'DEPARTMENT',
  DESIGNATION = 'DESIGNATION',
  INDUSTRY = 'INDUSTRY',
  RESUME = 'RESUME',
  EXPERIENCE_LEVEL = 'EXPERIENCE_LEVEL',
  SKILLS = 'SKILLS',
  LINKEDIN = 'LINKEDIN',
  GITHUB = 'GITHUB',
  PORTFOLIO = 'PORTFOLIO',

  // Contact & Preferences
  WHATSAPP = 'WHATSAPP',
  TWITTER = 'TWITTER',
  CONTACT_METHOD = 'CONTACT_METHOD',
  PREFERRED_TIME = 'PREFERRED_TIME',
  TIMEZONE = 'TIMEZONE',
  LANGUAGE = 'LANGUAGE',

  // Date & Time
  DOB = 'DOB',
  JOINING_DATE = 'JOINING_DATE',
  AVAILABILITY_DATE = 'AVAILABILITY_DATE',
  RESIGNATION_DATE = 'RESIGNATION_DATE',

  // Financial
  CURRENCY = 'CURRENCY',
  SALARY = 'SALARY',
  INCOME = 'INCOME',
  BUDGET = 'BUDGET',
  DISCOUNT = 'DISCOUNT',
  TAX_PERCENTAGE = 'TAX_PERCENTAGE',

  // Selection Fields
  STATUS = 'STATUS',
  TAGS = 'TAGS',
  GENDER = 'GENDER',
  MARITAL_STATUS = 'MARITAL_STATUS',
  ACCESS_LEVEL = 'ACCESS_LEVEL',

  // Legal / Agreement
  AGREEMENT = 'AGREEMENT',
  CONSENT = 'CONSENT',

  // Media Upload
  PROFILE_PICTURE = 'PROFILE_PICTURE',

  // Descriptive
  NOTES = 'NOTES',
  DESCRIPTION = 'DESCRIPTION',
}

export enum ImportTypeEnum {
  NEW = "NEW",
  BULK = "BULK_UPDATE",
}


export interface FieldConfig {
  uiMeta: {
    // General UI
    hideLabel?: boolean;
    fieldLabel?: string;
    // Display name to show on forms (separate from internal field name)
    displayName?: string;
    toolTipText?: string;
    hideField?: boolean;
    disabled?: boolean;
    placeholder?: string;
    descriptionContent?: string;
    layout?: string;
    separatorOption?: 'none' | 'comma' | 'space';
    decimalPlaces?: number;
    includeTimeInDate?: boolean;
    // Currency
    currency?: string;
    currencyDisplay?: string;

    // Month-Year
    displayMonths?: string[];
    allowedMonthYears?: string;

    // File/Image Upload
    allowedFormats?: string;

    // Units & Measurement
    unit?: string;

    // Validation / Behavior
    isRequired?: boolean;
    autoFill?: boolean;
    characterLimitEnabled?: boolean;
    maxLength?: number;
    minEnabled?: boolean;
    minValue?: number;

    // Range
    rangeEnabled?: boolean;
    minRange?: number | string;
    maxRange?: number | string;

    // Choice fields
    minChoices?: number;
    maxChoices?: number;
    // Numbers: allow decimal input when enabled
    allowDecimal?: boolean;

    // Date restrictions
    allowedDates?: {
      min?: string;
      max?: string;
    };
    allowedDaysOfWeek?: string;

    // Time picker
    timeFormat: '12' | '24';
    minuteInterval?: number;
    initialTime?: {
      hh: string;
      mm: string;
      ap: string;
    };

    // Sliders
    minValueSlider?: number;
    maxValueSlider?: number;
    showStepValues?: boolean;
    showStepPointers?: boolean;
    showMinMaxLabels?: boolean;
    // Choice settings
    useGlobalList?: boolean;
    choicesFieldType?: string;
    choiceSortOrder?: 'none' | 'asc' | 'desc';
    // Whether to include an 'Other' option for choice fields
    includeOtherOption?: boolean;
    // Default selection for multiple choices (single value)
    defaultSelection?: string;

    // Add these new properties:
    initialValue?: string;
    weekStartDay?: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

    // File upload
    uploadLimitMin?: number;
    uploadLimitMax?: number;
    fileSizeMin?: number;
    fileSizeMax?: number;
    fileSizeMinUnit?: 'KB' | 'MB' | 'GB';
    fileSizeMaxUnit?: 'KB' | 'MB' | 'GB';

    // Image upload
    uploadLimitMinImage?: number;
    uploadLimitMaxImage?: number;
    fileSizeMinImage?: number;
    fileSizeMaxImage?: number;
    fileSizeMinUnitImage?: 'KB' | 'MB' | 'GB';
    fileSizeMaxUnitImage?: 'KB' | 'MB' | 'GB';
    imageFormatTypeImage?: string;
    cameraSelectionImage?: string;
    selectedUserInputsImage?: string[];

    // Slider
    fieldSize?: string;
    initialSliderValue?: number;
    stepValue?: number;
    showMinMaxLabel?: boolean;

    // Audio/Video
    audioVideoFiles?: string[];
  };
  options?: string[];
};
export const DEFAULT_BASIC_CONFIG: FieldConfig = {
  uiMeta: {
    // General UI
    hideLabel: false,
    hideField: false,
    disabled: false,
    displayName: "",
    placeholder: "",
    descriptionContent: "",
    layout: "",
    separatorOption: "none",

    // Currency
    currency: "",
    currencyDisplay: "",

    // Month-Year
    displayMonths: [],
    allowedMonthYears: "",

    // File/Image Upload
    allowedFormats: "",

    // Units & Measurement
    unit: "",

    // Validation / Behavior
    isRequired: false,
    autoFill: false,
    characterLimitEnabled: false,
    maxLength: undefined,
    minEnabled: false,
    minValue: undefined,

    // Range
    rangeEnabled: false,
    minRange: undefined,
    maxRange: undefined,

    // Choice fields
    minChoices: undefined,
    maxChoices: undefined,

    // Date restrictions
    allowedDates: {
      min: '',
      max: '',
    },
    allowedDaysOfWeek: "",

    // Time picker
    timeFormat: "12",
    minuteInterval: undefined,
    initialTime: {
      hh: "",
      mm: "",
      ap: "",
    },

    // Sliders
    minValueSlider: undefined,
    maxValueSlider: undefined,
    showStepValues: false,
    showStepPointers: false,
    showMinMaxLabels: false,
    // Choice settings
    useGlobalList: false,
    choicesFieldType: "",
    choiceSortOrder: "none",
    includeOtherOption: false,
    defaultSelection: '',
    allowDecimal: false,

    initialValue: '',
    weekStartDay: 'sunday',

    // File upload
    uploadLimitMin: undefined,
    uploadLimitMax: undefined,
    fileSizeMin: undefined,
    fileSizeMax: undefined,
    fileSizeMinUnit: 'KB',
    fileSizeMaxUnit: 'MB',

    // Image upload
    uploadLimitMinImage: undefined,
    uploadLimitMaxImage: undefined,
    fileSizeMinImage: undefined,
    fileSizeMaxImage: undefined,
    fileSizeMinUnitImage: 'MB',
    fileSizeMaxUnitImage: 'MB',
    imageFormatTypeImage: '',
    cameraSelectionImage: '',
    selectedUserInputsImage: [],

    // Slider
    fieldSize: 'Medium',
    initialSliderValue: undefined,
    stepValue: 1,
    showMinMaxLabel: true,

    // Audio/Video
    audioVideoFiles: [],
  },
  options: [],
};

export enum FieldDataTypeEnum {
  SINGLE_LINE = 'SINGLE_LINE',
  MULTI_LINE = 'MULTI_LINE',
  NUMBERS = 'NUMBERS',
  SELECT = 'SELECT',
  RADIO = 'RADIO',
  CHECKBOX = 'CHECKBOX',
  MULTIPLE_CHOICES = 'MULTIPLE_CHOICES',
  DATE = 'DATE',
  TIME = 'TIME',
  DATE_TIME = 'DATE_TIME',
  MONTH_YEAR = 'MONTH_YEAR',
  FILE_UPLOAD = 'FILE_UPLOAD',
  IMAGE_UPLOAD = 'IMAGE_UPLOAD',
  SLIDER = 'SLIDER',
  DESCRIPTION = 'DESCRIPTION',
  VIDEO_EMBED = 'VIDEO_EMBED',
  AUDIO_EMBED = 'AUDIO_EMBED',
  DISPLAY_ONLY = 'DISPLAY_ONLY',
};
export const ConditionalFields = [
  'RADIO', 'CHECKBOX', 'SELECT', 'NUMBERS'
];

export const FORM_BUILDER_TABS = [
  { id: 1, title: "System Fields", isStandard: true },
  { id: 2, title: "Custom Fields", isStandard: false },
  { id: 3, title: "Components", isStandard: false },
];

export interface EditingField {
  fieldId?: number;
  pageIndex: number;
  rowIndex: number;
  fieldIndex: number;
  config: FieldConfig | null;
  fieldType?: FieldType;
}

export type DeleteItemType = {
  type: 'page' | 'field' | 'component' | 'row';
  pageIndex?: number;
  rowIndex?: number;
  fieldIndex?: number;
  label?: string;
};

export interface IconProps {
  size?: number | string;
  width?: number | string;
  height?: number | string;
  color?: string;
  className?: string;
  onClick?: () => void;
}
export type RowDragData = {
  pageIndex: number;
  rowIndex: number;
};

export const defaultListingParams = {
  page: 1,
  perPage: 10,
  filters: {},
  query: '',
  visibleColumns: [],
};


export interface Scope {
  id: number;
  name: string;
  displayName: string;
  description: string;
  isDeleted: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FieldScope {
  id: number;
  displayName: string;
  fieldId: number;
  isDeleted: boolean;
  scopeId: string;
  scope: Scope;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// Merge Contacts & Organizations Types

interface Organization {
  data_id: number,
  // id: number,
  // name: string,
  // image: string,
  orgIds: number[],
  orgNames: string,
  // orgRoleIds: number[]
}
interface Roles {
  roleids: number[];
  roleNames: string;
  data_id: number;
}
interface FormSubmissions {
  id: number,
  name: string,
  value: string;
  data_id: number;
}
interface metaType {
  data_id: number;
  value: string;
}
export interface Data {
  metaData: {
    id: number;
    image: string;
    firstName?: metaType;
    lastName?: metaType;
    fullName?: metaType;
    email?: metaType;
    contactOrganizations?: Organization;
    // organization?: Organization;
    // contactRoles?: Roles;
    name?: {
      data_id: number;
      value: string;
    };
  }
  formSubmissions: FormSubmissions[];
  Ids: number[];
  privateNotes?: { data_id: number; pNotesCount: number; }[];
  contacts?: { data_id: number; contactsCount: number; }[];
}


export interface ImportRecord {
  id: number;
  scopeId: number;
  accountId: number;
  fileKey: string;
  companyId: number;
  statusId: number;
  isDeleted: boolean;
  fileName: string;
  meta: {
    originalFileName: string;
    importType: string;
    fileType: string;
    fileSize: number;
    columnMapping: Record<string, string>;
    errorFileKey: string | null;
    duplicateFileKey: string | null;
    duplicateCount: number;
    errorCount: number;
    totalProcessed: number;
    totalRecords: number;
  };
  createdAt: string; // ISO Date string
  updatedAt: string; // ISO Date string
  scope: {
    id: number;
    name: string;
    displayName: string;
  };
  account: {
    id: number;
    firstName: string;
    lastName: string;
    image: string;
    signedUrl: string;
  };
}


// Basic File type
export type FileType = {
  name: string;
  size: number;       // in bytes
  type: string;       // MIME type, e.g. "image/png"
  lastModified: number;
  url?: string;       // optional: preview URL
  file?: File;
};


// Contact and organzation Data types

interface submissionValues {
  field: {
    config: {
      options: string[];
    };
    dataType: string;
    id: number;
    name: string;
  };
  id: number;
  value: string;
}
export interface crmFormReference {
  accessKey: string;
  crmFieldValues: submissionValues[];
  formSubmissions: {
    id: number;
    submissionValues: submissionValues[];
  };
  id: number;
}


interface Role {
  id: number;
  name: string;
}

interface ContactOrgRole {
  id: number;
  contactOrgId: number;
  roleId: number;
  isDeleted: boolean;
  role: Role;
}

interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  image: string | null;
  email: string;
  signedUrl: string;
}

export interface ContactOrganization {
  id: number;
  contactId: number;
  organizationId: number;
  isPrimary: boolean;
  isDeleted: boolean;
  contact: Contact;
  contactOrgRoles: ContactOrgRole[];
}


export type OrganizationProps = {
  id: number;
  signedUrl?: string;
  account?: {
    firstName?: string;
    lastName?: string;
  };
  createdBy?: {
    firstName?: string;
    lastName?: string;
  };
  contactOrganizations?: ContactOrganization[];
  name?: string;
  website?: string;
  industry?: string;
  employeeRange?: string;
  contacts?: any;
  cfr?: any;
  source: string;
  createdAt?: Date;
  updatedAt?: Date;
  lastModifiedByAccount?: {
    firstName?: string;
    lastName?: string;
  };
};




export interface WebsiteFormValues {
  websiteName: string;
  websiteURl: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  fontFamily: string;
  logoPreview: string;
  timezone: string;
  loginDescription: string;
  loginHeading: string;
  active?: boolean;
  favicon?: string;
  faviconPreview?: string;
  termOfService?: string;
  privacyPolicy?: string;
  cookieEnabled?: boolean;
  headerHtml?: string;
  customCss?: string;
  customJs?: string;
  fileName?: string;
  fileSize?: number;
  logoFileName?: string;
  logoFileSize?: number;
}


















// Page Builder types

// Element Template from API
export interface ElementTemplate {
  id: number;
  name: string;
  accessKey: string;
  statusId: number;
  createdAt: string;
  updatedAt: string;
  defaultSettings: {
    html: {
      tag: string;
      content?: string | any;
      attributes: Record<string, any>;
    };
    styleSettings: Record<string, any>;
  };
}

export interface ElementTemplatesResponse {
  message: string;
  count: number;
  templates: ElementTemplate[];
}

// Canvas element types
export type ElementType =
  | 'paragraph'
  | 'heading'
  | 'footer'
  | 'header'
  | 'separator'
  | 'media'
  | 'image'
  | 'video'
  | 'form'
  | 'button'
  | 'search_query'
  | 'single_column'
  | 'multi_column';

export interface Element {
  id: string;
  type: ElementType;
  content?: string;
  properties?: Record<string, any>;
  defaultSettings?: any;
}

export interface Section {
  id: string;
  type: 'single_column' | 'multi_column';
  elements: Element[];
  columns?: {
    left: Element[];
    right: Element[];
  };
  properties?: Record<string, any>;
}

export type CanvasItem = Element | Section;

export interface SettingsPanelConfig {
  type: 'element' | 'section' | 'column-element' | 'header' | 'footer';
  itemId: string;
  itemType?: string;
  sectionId?: string;
  column?: 'left' | 'right';
}

export interface TempSettings {
  [key: string]: any;
}

export interface DragState {
  draggedElement: string | null;
  isOverCanvas: boolean;
  isOverSection: string | null;
  isOverColumn: { sectionId: string; column: 'left' | 'right' } | null;
}

// Menu types for navigation
export interface MenuPage {
  id: number;
  name: string;
  url: string;
  visibility: string;
  statusId: number;
}

export interface MenuItem {
  id: number;
  pageId: number;
  position: number;
  page: MenuPage;
  children?: MenuItem[];
}

export interface Menu {
  id: number;
  accessKey: 'header' | 'footer';
  pages: MenuItem[];
  createdAt: string;
  updatedAt: string;
}


export interface OrganizationInfo {
  name: string;
  email: string;
  website: string;
  organizationId: string;
  logoUrl?: string;
  industry?: string;
  creationDate?: string;
  createdBy?: string;
  isPrimary?: boolean;
  source?: string;
  roles?: string[];
}

export interface AboutDetails {
  industry: string;
  owner: string;
  region: string;
  employeeRange: string;
}

export interface CreationDetails {
  createdBy: string;
  creationDate: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
}

export interface OrganizationData {
  info: OrganizationInfo;
  about: AboutDetails;
  creation: CreationDetails;
}


// export interface OrganizationInfo {
//   name: string;
//   email: string;
//   website: string;
//   industry: string;
//   creationDate: string;
//   createdBy: string;
//   organizationId: string;
//   logoUrl?: string;
//   isPrimary?: boolean;
//   source: string;
// }

export interface OrganizationCardProps {
  OrganizationInfo: OrganizationInfo;
  onViewDetails?: () => void;
}